import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const codeRefs = useRef([]);
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Ingresá tu email'); return; }
    setLoading(true);
    try {
      await authService.forgotPassword(email.trim());
      setStep(2);
    } catch {
      setError('Error al enviar el código. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const digits = code.split('');
    while (digits.length < 6) digits.push('');
    digits[index] = value;
    setCode(digits.join(''));

    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      e.preventDefault();
      setCode(pasted.padEnd(6, '').slice(0, 6));
      const focusIdx = Math.min(pasted.length, 5);
      codeRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    const cleanCode = code.replace(/\s/g, '');
    if (cleanCode.length !== 6) { setError('Ingresá el código de 6 dígitos'); return; }
    setLoading(true);
    try {
      const res = await authService.verifyResetCode(email.trim(), cleanCode);
      if (res.data.valid) {
        setStep(3);
      } else {
        setError('Código incorrecto o expirado');
      }
    } catch {
      setError('Error al verificar el código');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    if (newPassword !== confirmPassword) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    try {
      await authService.resetPassword(email.trim(), code.replace(/\s/g, ''), newPassword);
      setSuccess('Contraseña actualizada correctamente');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Recuperar contraseña</h1>

        {step === 1 && (
          <>
            <p className="auth-subtitle">
              Ingresá tu email y te enviaremos un código de verificación.
            </p>
            <form onSubmit={handleSendCode}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
                style={{ marginTop: '0.5rem' }}
              >
                {loading ? <><span className="spinner" /> Enviando...</> : 'Enviar código'}
              </button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <p className="auth-subtitle">
              Ingresá el código de 6 dígitos que enviamos a <strong>{email}</strong>
            </p>
            <form onSubmit={handleVerifyCode}>
              <div style={{
                display: 'flex', gap: '0.5rem', justifyContent: 'center',
                margin: '1.5rem 0',
              }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    ref={(el) => (codeRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={code[i] || ''}
                    onChange={(e) => handleCodeInput(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    onPaste={i === 0 ? handleCodePaste : undefined}
                    autoFocus={i === 0}
                    style={{
                      width: 48, height: 56, textAlign: 'center',
                      fontSize: '1.5rem', fontWeight: 600,
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                    }}
                  />
                ))}
              </div>
              {error && <div className="error-message">{error}</div>}
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
              >
                {loading ? <><span className="spinner" /> Verificando...</> : 'Verificar código'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={() => { setStep(1); setCode(''); setError(''); }}
                style={{ marginTop: '0.75rem' }}
              >
                Reenviar código
              </button>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <p className="auth-subtitle">Ingresá tu nueva contraseña.</p>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>Nueva contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete="new-password"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Confirmar contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repetí la contraseña"
                  autoComplete="new-password"
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
                style={{ marginTop: '0.5rem' }}
              >
                {loading ? <><span className="spinner" /> Guardando...</> : 'Cambiar contraseña'}
              </button>
            </form>
          </>
        )}

        <div className="auth-footer">
          <Link to="/login">Volver al inicio de sesión</Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
